import React from "react";
import SkeletonElement from "./SkeletonElement";

interface ChartSkeletonProps {
  type: "pie" | "line" | "radar" | "bar";
  className?: string;
}

export const PieChartSkeleton: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div
      className={`flex w-full min-h-[222px] flex-col items-center justify-center ${className}`}
      data-testid="pie-chart-skeleton"
    >
      {/* Circular skeleton */}
      <SkeletonElement height="h-48" width="w-48" rounded="rounded-full" />

      {/* Legend skeleton */}
      <div className="grid grid-rows-2 grid-flow-col auto-cols-max mt-4 gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="flex items-center gap-2">
            <SkeletonElement height="h-2" width="w-2" rounded="rounded-full" />
            <SkeletonElement height="h-3" width="w-16" />
          </div>
        ))}
      </div>
    </div>
  );
};

export const LineChartSkeleton: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div className={`flex w-full flex-col ${className}`}>
      {/* Chart area skeleton */}
      <div className="relative w-full h-[222px] bg-white border border-gray-200 rounded-lg p-4">
        {/* Grid lines */}
        <div className="absolute inset-0 flex flex-col justify-between">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="w-full h-px bg-gray-100" />
          ))}
        </div>

        {/* Line skeleton */}
        <div className="absolute inset-0 flex items-end justify-between px-4 pb-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonElement key={index} height="h-8" width="w-1" />
          ))}
        </div>

        {/* X-axis labels skeleton */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-between px-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonElement key={index} height="h-3" width="w-8" />
          ))}
        </div>
      </div>

      {/* Legend skeleton */}
      <div className="flex justify-center gap-4 mt-4">
        {Array.from({ length: 2 }).map((_, index) => (
          <div key={index} className="flex items-center gap-2">
            <SkeletonElement height="h-2" width="w-2" rounded="rounded-full" />
            <SkeletonElement height="h-3" width="w-20" />
          </div>
        ))}
      </div>
    </div>
  );
};

export const BarChartSkeleton: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div className={`flex w-full min-w-0 flex-col ${className}`} data-testid="bar-chart-skeleton">
      <div className="flex h-[222px] w-full min-w-0 flex-col justify-center gap-3">
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="flex w-full min-w-0 items-center gap-2">
            <SkeletonElement height="h-3" width="w-16" className="shrink-0" />
            <div className="min-w-0 flex-1">
              <SkeletonElement height="h-4" width={index % 2 === 0 ? 75 : 50} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const RadarChartSkeleton: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div className={`flex w-full flex-col items-center ${className}`} data-testid="radar-chart-skeleton">
      <SkeletonElement height="h-64" width="w-64" rounded="rounded-full" />
      <div className="flex justify-center gap-4 mt-4">
        {Array.from({ length: 2 }).map((_, index) => (
          <div key={index} className="flex items-center gap-2">
            <SkeletonElement height="h-2" width="w-2" rounded="rounded-full" />
            <SkeletonElement height="h-3" width="w-20" />
          </div>
        ))}
      </div>
    </div>
  );
};

export const ChartSkeleton: React.FC<ChartSkeletonProps> = ({ type, className }) => {
  if (type === "pie") {
    return <PieChartSkeleton className={className} />;
  }

  if (type === "radar") {
    return <RadarChartSkeleton className={className} />;
  }

  if (type === "bar") {
    return <BarChartSkeleton className={className} />;
  }

  return <LineChartSkeleton className={className} />;
};

export default ChartSkeleton;
